import React, { useState, Fragment } from 'react'
import { Drawer, DrawerContent, DrawerTrigger } from '../ui/drawer'
// import GameIconButton from '../GameIconButton/GameIconButton'
import { GameIconButton } from '@themed-components';
import { MdOutlineLeaderboard } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { MdOutlineEmojiEvents, MdOutlineTableBar, MdOutlineCasino } from "react-icons/md";
import styles from './TourneyRankingLeaderboard.module.css';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '../ui/sheet';
import useTourneyRankingLeaderboard from '@/src/hooks/data/useTourneyRankingLeaderboard';
import { useSelector } from 'react-redux';
import {
    checkToDisplayValueInK,
    checkToDisplayValueInL,
    parseDecimalValuesOfNumber,
} from '@/Common/utils/GameUtils';
import { doesExist } from '@/Common/utils/GameUtils';

// Constants
const TOURNEY_RANKING_TAB = "ranking";
const TOURNEY_PRIZE_TAB = "prize";
const TOURNEY_TABLES_TAB = "tables";
const TOURNEY_BLINDS_TAB = "blinds";

export default function TourneyRankingLeaderboard({ tempTableId }) {
    const { tourneyRankingLeaderboardDetails, gameDefinition: { smallBlind, bigBlind, payoutType, prizePoolType, isFlightTourney,
        isFlightParentTourney,
        parentTourneyName,
        bountyTourney, }, loading, requestTourneyRankingData } = useTourneyRankingLeaderboard(tempTableId);
    const [selectedTab, setSelectedTab] = useState(TOURNEY_RANKING_TAB);

    // Redux selectors
    const playerName = useSelector((state) => state.lobby.player.userName);
    const BBDisplay = useSelector((state) => state.gameTable.tableSettings.BBDisplay);
    const currentActiveGame = useSelector((state) => state.gameTable.games[tempTableId]);
    const watchPlayer = useSelector((state) => state.gameTable.games[tempTableId]?.watchPlayer);

    // console.log('Spruha log >>> Tourney Ranking Leaderboard Details >>> ', tourneyRankingLeaderboardDetails)

    const handleTabChange = (tab) => {
        if (selectedTab !== tab) {
            setSelectedTab(tab);
        }
    };

    const getTabItems = () => {
        const tabItems = [
            {
                label: "Ranking",
                value: TOURNEY_RANKING_TAB,
                icon: <MdOutlineLeaderboard />
            },
            {
                label: "Prize",
                value: TOURNEY_PRIZE_TAB,
                icon: <MdOutlineEmojiEvents />
            },
            {
                label: "Tables",
                value: TOURNEY_TABLES_TAB,
                icon: <MdOutlineTableBar />
            },
            {
                label: "Blinds",
                value: TOURNEY_BLINDS_TAB,
                icon: <MdOutlineCasino />
            },
        ];

        if (isFlightTourney && !isFlightParentTourney) {
            return tabItems.filter((tab) => tab.value !== TOURNEY_PRIZE_TAB);
        }

        return tabItems;
    };

    const formatCurrency = (amount) => {
        if (!amount) return "₹0";
        if (amount >= 100000) {
            return `₹${(amount / 100000).toFixed(1)}L`;
        } else if (amount >= 1000) {
            return `₹${(amount / 1000).toFixed(1)}K`;
        }
        return `₹${amount}`;
    };

    const formatChips = (chips) => {
        if (!chips) return "0";
        if (chips >= 100000) {
            return `${(chips / 100000).toFixed(1)}L`;
        } else if (chips >= 1000) {
            return `${(chips / 1000).toFixed(1)}K`;
        }
        return chips.toString();
    };

    const renderRankingTab = () => {
        const { rankDataList } = tourneyRankingLeaderboardDetails || {};
        if (!rankDataList || rankDataList.length === 0) {
            return (
                <div className={styles.tabContent}>
                    <div className={styles.noData}>No ranking data available</div>
                </div>
            );
        }

        return (
            <div className={styles.tabContent}>
                <div className={styles.tableHeader}>
                    <div className={`${styles.headerCell} ${styles.playerCell}`}>Players</div>
                    <div className={styles.headerCell}>Rank</div>
                    <div className={styles.headerCell}>Stack</div>
                    {bountyTourney && <div className={styles.headerCell}>Bounty</div>}
                </div>
                {rankDataList.map((player, index) => (
                    <Fragment key={player.playerName}>
                        <div className={`${styles.tableRow} ${player.playerName === playerName ? styles.currentUser : ""}`}>
                            <div className={`${styles.cell} ${styles.playerCell}`}>
                                <span className={player.isBustedOut ? styles.bustedOut : ""}>
                                    {player.displayName || player.playerName}
                                </span>
                            </div>
                            <div className={styles.cell}>
                                <span className={player.isBustedOut ? styles.bustedOut : ""}>
                                    {player.rank > 0 ? player.rank : "-"}
                                </span>
                            </div>
                            <div className={styles.cell}>
                                <span className={player.isBustedOut ? styles.bustedOut : ""}>
                                    {BBDisplay
                                        ? parseDecimalValuesOfNumber(player.playerChips / bigBlind, 1) + " BB"
                                        : formatChips(player.playerChips)
                                    }
                                </span>
                            </div>
                            {bountyTourney && (
                                <div className={styles.cell}>
                                    <span className={player.isBustedOut ? styles.bustedOut : ""}>
                                        {player.bountyAmount > 0 && player.noOfKnockedOutPlayers > 0
                                            ? `${formatCurrency(player.bountyAmount)} (${player.noOfKnockedOutPlayers})`
                                            : "-"}
                                    </span>
                                </div>
                            )}
                        </div>
                        {index < rankDataList.length - 1 && <div className={styles.separator} />}
                    </Fragment>
                ))}
            </div>
        );
    };

    const renderPrizeTab = () => {
        const { winnersPayoutWithBonus } = tourneyRankingLeaderboardDetails || {};
        if (!winnersPayoutWithBonus || Object.keys(winnersPayoutWithBonus).length === 0) {
            return (
                <div className={styles.tabContent}>
                    <div className={styles.noData}>No prize data available</div>
                </div>
            );
        }

        const positionsList = Object.keys(winnersPayoutWithBonus);
        return (
            <div className={styles.tabContent}>
                <div className={styles.tableHeader}>
                    <div className={styles.headerCell}>Position</div>
                    <div className={styles.headerCell}>Prize</div>
                </div>
                {positionsList.map((position, index) => {
                    const prize = winnersPayoutWithBonus[position];
                    return (
                        <Fragment key={position}>
                            <div className={styles.tableRow}>
                                <div className={styles.cell}>{position}</div>
                                <div className={styles.cell}>
                                    {prize.amount > 0 && formatCurrency(prize.amount)}
                                    {prize.bonus > 0 && (
                                        <>
                                            {prize.amount > 0 && <span className={styles.addIcon}>+</span>}
                                            {formatCurrency(prize.bonus)} (Bonus)
                                        </>
                                    )}
                                    {prize.ticket && (
                                        <>
                                            {(prize.amount > 0 || prize.bonus > 0) && <span className={styles.addIcon}>+</span>}
                                            <span className={styles.ticketIcon}>🎫</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            {index < positionsList.length - 1 && <div className={styles.separator} />}
                        </Fragment>
                    );
                })}
                {prizePoolType === "Dynamic" && (
                    <div className={styles.dynamicNote}>
                        Note: The prize breakdown may change based on the number of entries.
                    </div>
                )}
            </div>
        );
    };

    const renderTablesTab = () => {
        const { tableDetailsForClientList } = tourneyRankingLeaderboardDetails || {};
        if (!tableDetailsForClientList || tableDetailsForClientList.length === 0) {
            return (
                <div className={styles.tabContent}>
                    <div className={styles.noData}>No table data available</div>
                </div>
            );
        }

        let originalTableDetails = [...tableDetailsForClientList];
        if (originalTableDetails.length > 0 && currentActiveGame?.tableId) {
            let index = tableDetailsForClientList.findIndex(
                (item) => item.tableID === currentActiveGame.tableId
            );

            if (index !== -1) {
                let currentTableDetails = tableDetailsForClientList[index];
                let updatedTablesList = tableDetailsForClientList.filter(
                    (eachTable) => eachTable.tableID !== currentActiveGame.tableId
                );
                originalTableDetails = [currentTableDetails, ...updatedTablesList];
            }
        }

        return (
            <div className={styles.tabContent}>
                <div className={styles.tableHeader}>
                    <div className={styles.headerCell}>Table</div>
                    <div className={styles.headerCell}>Players</div>
                    <div className={styles.headerCell}>Largest</div>
                    <div className={styles.headerCell}>Smallest</div>
                </div>
                {originalTableDetails.map((table, index) => (
                    <Fragment key={table.tableID}>
                        <div className={styles.tableRow}>
                            <div className={styles.cell}>
                                <span className={styles.tableId}>Table #{table.tableID?.slice(-2)}</span>
                            </div>
                            <div className={styles.cell}>{table.numberOfPlayers}</div>
                            <div className={styles.cell}>
                                {BBDisplay
                                    ? parseDecimalValuesOfNumber(table.highestChips / bigBlind, 1) + " BB"
                                    : formatChips(table.highestChips)
                                }
                            </div>
                            <div className={styles.cell}>
                                {BBDisplay
                                    ? parseDecimalValuesOfNumber(table.lowestChips / bigBlind, 1) + " BB"
                                    : formatChips(table.lowestChips)
                                }
                            </div>
                        </div>
                        {index < originalTableDetails.length - 1 && <div className={styles.separator} />}
                    </Fragment>
                ))}
            </div>
        );
    };

    const renderBlindsTab = () => {
        let { blindStructure, levelOffset } = tourneyRankingLeaderboardDetails || {};
        if (!blindStructure || blindStructure.length === 0) {
            return (
                <div className={styles.tabContent}>
                    <div className={styles.noData}>No blind structure data available</div>
                </div>
            );
        }

        if (isFlightTourney) {
            if (isFlightParentTourney) {
                blindStructure = blindStructure.slice(levelOffset);
            } else {
                blindStructure = blindStructure.slice(0, levelOffset);
            }
        }

        return (
            <div className={styles.tabContent}>
                <div className={styles.tableHeader}>
                    <div className={styles.headerCell}>Level</div>
                    <div className={styles.headerCell}>SB</div>
                    <div className={styles.headerCell}>BB</div>
                    <div className={styles.headerCell}>Ante</div>
                </div>
                {blindStructure.map((blind, index) => (
                    <Fragment key={blind.level}>
                        <div className={`${styles.tableRow} ${blind.level === tourneyRankingLeaderboardDetails?.currentLevel ? styles.currentLevel : ""}`}>
                            <div className={styles.cell}>{blind.level}</div>
                            <div className={styles.cell}>
                                {parseDecimalValuesOfNumber(
                                    blind.sb,
                                    2,
                                    checkToDisplayValueInK(blind.sb),
                                    checkToDisplayValueInL(blind.sb)
                                )}
                            </div>
                            <div className={styles.cell}>
                                {parseDecimalValuesOfNumber(
                                    blind.bb,
                                    2,
                                    checkToDisplayValueInK(blind.bb),
                                    checkToDisplayValueInL(blind.bb)
                                )}
                            </div>
                            <div className={styles.cell}>
                                {blind.ante > 0
                                    ? parseDecimalValuesOfNumber(
                                        blind.ante,
                                        2,
                                        checkToDisplayValueInK(blind.ante),
                                        checkToDisplayValueInL(blind.ante)
                                    )
                                    : "-"
                                }
                            </div>
                        </div>
                        {index < blindStructure.length - 1 && <div className={styles.separator} />}
                    </Fragment>
                ))}
            </div>
        );
    };

    const renderSelectedTab = () => {
        switch (selectedTab) {
            case TOURNEY_RANKING_TAB:
                return renderRankingTab();
            case TOURNEY_PRIZE_TAB:
                return renderPrizeTab();
            case TOURNEY_TABLES_TAB:
                return renderTablesTab();
            case TOURNEY_BLINDS_TAB:
                return renderBlindsTab();
            default:
                return renderRankingTab();
        }
    };

    return (
        <Sheet>
            <SheetTrigger>
                <GameIconButton onClick={() => requestTourneyRankingData()}>
                    {
                        doesExist(tourneyRankingLeaderboardDetails?.playerRank) && tourneyRankingLeaderboardDetails.playerRank > 0 && tourneyRankingLeaderboardDetails.currRegistration > 0
                            ? <div className={styles.rankLabelContainer}>
                                <span className={styles.rankLabel}>Rank: </span>
                                <span className={styles.rankValue}>
                                    {`${tourneyRankingLeaderboardDetails.playerRank}/${tourneyRankingLeaderboardDetails.currRegistration}`}
                                </span>
                                </div>
                            : null
                    }
                    <MdOutlineLeaderboard />
                </GameIconButton>
            </SheetTrigger>
            <SheetContent className={styles.tourneyRankingLeaderboardDrawerContent} side="right">
                <div className={styles.container}>
                    {/* Header */}
                    <div className={styles.header}>
                        <h2 className={styles.title}>{tourneyRankingLeaderboardDetails?.tourneyName || "Tournament"}</h2>
                        <SheetClose className={styles.closeButton}>
                            <IoClose />
                        </SheetClose>
                    </div>

                    {!tourneyRankingLeaderboardDetails ? (
                        <div className={styles.loading}>
                            <div className={styles.spinner}></div>
                            <p>Loading tournament data...</p>
                        </div>
                    ) : tourneyRankingLeaderboardDetails ? (
                        <div className={styles.content}>
                            {/* Tournament Details */}
                            <div className={styles.tourneyDetails}>
                                <div className={styles.prizePool}>
                                    <div className={styles.trophyIcon}>🏆</div>
                                    <div className={styles.prizeInfo}>
                                        <span className={styles.prizeLabel}>
                                            {isFlightTourney && !isFlightParentTourney ? "Qualifier for" : "Prizes Worth"}
                                        </span>
                                        <span className={styles.prizeAmount}>
                                            {isFlightTourney && !isFlightParentTourney
                                                ? parentTourneyName
                                                : formatCurrency(tourneyRankingLeaderboardDetails.prizePool)
                                            }
                                        </span>
                                        {tourneyRankingLeaderboardDetails.prizePoolTickets > 0 && (
                                            <span className={styles.ticketInfo}>
                                                + {tourneyRankingLeaderboardDetails.prizePoolTickets} 🎫
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.statsRow}>
                                    {!isFlightTourney && (
                                        <>
                                            <div className={styles.statItem}>
                                                <span className={styles.statLabel}>Winners</span>
                                                <span className={styles.statValue}>{tourneyRankingLeaderboardDetails.totalNumberOfWinners}</span>
                                            </div>
                                            <div className={styles.verticalSeparator} />
                                        </>
                                    )}

                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>Your Rank</span>
                                        <span className={styles.statValue}>
                                            {watchPlayer
                                                ? "-"
                                                : `${tourneyRankingLeaderboardDetails.playerRank}/${tourneyRankingLeaderboardDetails.currRegistration}`
                                            }
                                        </span>
                                    </div>

                                    <div className={styles.verticalSeparator} />

                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>Entries</span>
                                        <span className={styles.statValue}>
                                            {parseDecimalValuesOfNumber(
                                                tourneyRankingLeaderboardDetails.currRegistration,
                                                2,
                                                checkToDisplayValueInK(tourneyRankingLeaderboardDetails.currRegistration),
                                                checkToDisplayValueInL(tourneyRankingLeaderboardDetails.currRegistration)
                                            )}
                                        </span>
                                        <div className={styles.activePlayers}>
                                            <span className={styles.playerIcon}>👤</span>
                                            <span className={styles.activeCount}>
                                                {parseDecimalValuesOfNumber(
                                                    tourneyRankingLeaderboardDetails.qualifiedPlayersCount,
                                                    2,
                                                    checkToDisplayValueInK(tourneyRankingLeaderboardDetails.qualifiedPlayersCount),
                                                    checkToDisplayValueInL(tourneyRankingLeaderboardDetails.qualifiedPlayersCount)
                                                )}
                                            </span>
                                            <span className={styles.activeLabel}>Active</span>
                                        </div>
                                    </div>

                                    {isFlightParentTourney && tourneyRankingLeaderboardDetails.nextPrizeJumpRank > 0 && (
                                        <>
                                            <div className={styles.verticalSeparator} />
                                            <div className={styles.statItem}>
                                                <span className={styles.statLabel}>Current Payout</span>
                                                <span className={styles.statValue}>
                                                    {formatCurrency(
                                                        (tourneyRankingLeaderboardDetails.currentPrizeAmount?.amount || 0) +
                                                        (tourneyRankingLeaderboardDetails.currentPrizeAmount?.bonus || 0)
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Next Prize Jump */}
                                {isFlightParentTourney && tourneyRankingLeaderboardDetails.nextPrizeJumpRank > 0 && (
                                    <div className={styles.nextPrizeJump}>
                                        <div className={styles.separator} />
                                        <div className={styles.jumpInfo}>
                                            <span className={styles.jumpLabel}>Next jump:</span>
                                            <span className={styles.jumpValue}>
                                                {tourneyRankingLeaderboardDetails.nextPrizeJumpRank}
                                                {` (${formatCurrency(
                                                    (tourneyRankingLeaderboardDetails.nextPrizeAmount?.amount || 0) +
                                                    (tourneyRankingLeaderboardDetails.nextPrizeAmount?.bonus || 0)
                                                )})`}
                                                ⬆️
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Current Level Info */}
                                <div className={styles.levelInfo}>
                                    <div className={styles.levelSection}>
                                        <div className={styles.levelItem}>
                                            <span className={styles.levelLabel}>
                                                Current Level ({tourneyRankingLeaderboardDetails.currentLevel})
                                            </span>
                                            <span className={styles.levelValue}>
                                                {tourneyRankingLeaderboardDetails.currentLevel}/{tourneyRankingLeaderboardDetails.maxLevel}
                                            </span>
                                        </div>
                                        <div className={styles.levelItem}>
                                            <span className={styles.levelLabel}>Next Level</span>
                                            <span className={styles.levelValue}>
                                                {tourneyRankingLeaderboardDetails.nextLevel > tourneyRankingLeaderboardDetails.maxLevel
                                                    ? "-"
                                                    : `${tourneyRankingLeaderboardDetails.nextLevel}/${tourneyRankingLeaderboardDetails.maxLevel}`
                                                }
                                            </span>
                                        </div>
                                    </div>

                                    {tourneyRankingLeaderboardDetails.nextBlindIn >= 0 && (
                                        <div className={styles.blindsUp}>
                                            <span className={styles.blindsLabel}>Blinds up in </span>
                                            <span className={styles.blindsTime}>
                                                {tourneyRankingLeaderboardDetails.nextBlindIn === 0
                                                    ? "<1"
                                                    : tourneyRankingLeaderboardDetails.nextBlindIn
                                                } m ⬆️
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Chip Stats */}
                                <div className={styles.chipStats}>
                                    <div className={styles.chipHeader}>
                                        <span className={styles.chipIcon}>🪙</span>
                                        <span className={styles.chipTitle}>Chips</span>
                                    </div>
                                    <div className={styles.chipRow}>
                                        <div className={styles.chipItem}>
                                            <span className={styles.chipLabel}>Largest</span>
                                            <span className={styles.chipValue}>
                                                {BBDisplay
                                                    ? parseDecimalValuesOfNumber(tourneyRankingLeaderboardDetails.highestChipStack / bigBlind, 1) + " BB"
                                                    : formatChips(tourneyRankingLeaderboardDetails.highestChipStack)
                                                }
                                            </span>
                                        </div>
                                        <div className={styles.chipItem}>
                                            <span className={styles.chipLabel}>Average</span>
                                            <span className={styles.chipValue}>
                                                {BBDisplay
                                                    ? parseDecimalValuesOfNumber(tourneyRankingLeaderboardDetails.averageChipStack / bigBlind, 1) + " BB"
                                                    : formatChips(tourneyRankingLeaderboardDetails.averageChipStack)
                                                }
                                            </span>
                                        </div>
                                        <div className={styles.chipItem}>
                                            <span className={styles.chipLabel}>Smallest</span>
                                            <span className={styles.chipValue}>
                                                {BBDisplay
                                                    ? parseDecimalValuesOfNumber(tourneyRankingLeaderboardDetails.lowestChipStack / bigBlind, 1) + " BB"
                                                    : formatChips(tourneyRankingLeaderboardDetails.lowestChipStack)
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Add-ons and Rebuys */}
                                {((tourneyRankingLeaderboardDetails.addonSettings && Object.keys(tourneyRankingLeaderboardDetails.addonSettings).length > 0) ||
                                    (tourneyRankingLeaderboardDetails.rebuySettings && Object.keys(tourneyRankingLeaderboardDetails.rebuySettings).length > 0) ||
                                    (tourneyRankingLeaderboardDetails.reEntrySettings && tourneyRankingLeaderboardDetails.reEntrySettings.noOfReentry > 0)) && (
                                        <div className={styles.addonsRebuys}>
                                            <div className={styles.separator} />

                                            {tourneyRankingLeaderboardDetails.addonSettings && Object.keys(tourneyRankingLeaderboardDetails.addonSettings).length > 0 && (
                                                <div className={styles.addonItem}>
                                                    <div className={styles.addonStat}>
                                                        <span className={styles.addonLabel}>Total Add-ons</span>
                                                        <span className={styles.addonValue}>{tourneyRankingLeaderboardDetails.totalAddOnCount}</span>
                                                    </div>
                                                    <div className={styles.addonStat}>
                                                        <span className={styles.addonLabel}>Add-on fee</span>
                                                        <span className={styles.addonValue}>{formatCurrency(tourneyRankingLeaderboardDetails.addonSettings.addonEntryFee)}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {tourneyRankingLeaderboardDetails.rebuySettings && Object.keys(tourneyRankingLeaderboardDetails.rebuySettings).length > 0 && (
                                                <div className={styles.rebuyItem}>
                                                    <div className={styles.rebuyStat}>
                                                        <span className={styles.rebuyLabel}>Total Re-buys</span>
                                                        <span className={styles.rebuyValue}>{tourneyRankingLeaderboardDetails.totalRebuyCount}</span>
                                                    </div>
                                                    <div className={styles.rebuyStat}>
                                                        <span className={styles.rebuyLabel}>Re-buy fee</span>
                                                        <span className={styles.rebuyValue}>{formatCurrency(tourneyRankingLeaderboardDetails.rebuySettings.rebuyEntryFee)}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {tourneyRankingLeaderboardDetails.reEntrySettings && tourneyRankingLeaderboardDetails.reEntrySettings.noOfReentry > 0 && (
                                                <div className={styles.rebuyItem}>
                                                    <div className={styles.rebuyStat}>
                                                        <span className={styles.rebuyLabel}>Total Re-Entries</span>
                                                        <span className={styles.rebuyValue}>{tourneyRankingLeaderboardDetails.totalReEntry}</span>
                                                    </div>
                                                    <div className={styles.rebuyStat}>
                                                        <span className={styles.rebuyLabel}>Re-entry fees</span>
                                                        <span className={styles.rebuyValue}>{formatCurrency(tourneyRankingLeaderboardDetails.reEntrySettings.reentryEntryFee)}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Rebuy/Re-entry descriptions */}
                                            {tourneyRankingLeaderboardDetails.rebuySettings?.noOfRebuys > 0 && (
                                                <div className={styles.rebuyDescription}>
                                                    {`${tourneyRankingLeaderboardDetails.rebuySettings.noOfRebuys} re-buys allowed till Level ${tourneyRankingLeaderboardDetails.rebuySettings.rebuyLevel}`}
                                                </div>
                                            )}

                                            {tourneyRankingLeaderboardDetails.reEntrySettings?.noOfReentry > 0 && (
                                                <div className={styles.rebuyDescription}>
                                                    {`${tourneyRankingLeaderboardDetails.reEntrySettings.noOfReentry} re-entries allowed till Level ${tourneyRankingLeaderboardDetails.reEntrySettings.reentryLevel}`}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                {/* Late Registration */}
                                {tourneyRankingLeaderboardDetails.lateReg && !tourneyRankingLeaderboardDetails.lateRegEnded && (
                                    <div className={styles.lateReg}>
                                        <span className={styles.lateRegDot}>🟠</span>
                                        <span className={styles.lateRegText}>
                                            Late registration available for {tourneyRankingLeaderboardDetails.lateRegEndTimeInMin} min
                                        </span>
                                    </div>
                                )}
                                {tourneyRankingLeaderboardDetails.lateRegEnded && (
                                    <div className={styles.lateReg}>
                                        <span className={styles.lateRegDot}>🟠</span>
                                        <span className={styles.lateRegText}>Late registration ended.</span>
                                    </div>
                                )}
                            </div>

                            {/* Tabs */}
                            <div className={styles.tabSection}>
                                <div className={styles.tabbar}>
                                    {getTabItems().map((tab) => (
                                        <button
                                            key={tab.value}
                                            className={`${styles.tab} ${selectedTab === tab.value ? styles.activeTab : ""}`}
                                            onClick={() => handleTabChange(tab.value)}
                                        >
                                            <span className={styles.tabIcon}>{tab.icon}</span>
                                            <span className={styles.tabLabel}>{tab.label}</span>
                                        </button>
                                    ))}
                                </div>

                                {renderSelectedTab()}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.noData}>
                            <p>No tournament data available</p>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    )
}
